import {
  Card,
  Box,
  CardContent,
  Typography,
  Chip,
  Grid,
  Stack,
  Divider,
  Skeleton,
} from "@mui/material";
import PatientDetailsStats from "./patientDetails";
import TabsWithActivity from "./tabsWithActivity";
import ActionButtons from "./actionButtons";
import { UserIcon } from "../../assets/common-svg/CommonSvg";
import { useEffect, useState } from "react";
import DashboardApi from "../../services/dashboardService";
import { useSelector } from "react-redux";
import { getUserHasAccess } from "../../utils/authorities";
import { useNavigate } from "react-router";
import NoDataCard from "../../components/no-data-card";
import Chart from "react-apexcharts";
import moment from "moment";
/* ================= PHASE STATISTICS ================= */


export const CardChart = ({ data }) => {
  const hasData = data?.seriesData?.some((item) => Number(item) > 0);

  return (
    <div
      className={!hasData ? "defsult-stroke-cricle" : ""}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {data && (
        <Chart
          type="donut"
          height={300}
          series={data.seriesData}
          options={{
            ...data.optionsData,

            tooltip: {
              enabled: true,
              y: {
                formatter: (val, { seriesIndex }) =>
                  `${data.countData?.[seriesIndex] || 0} phase`,
              },
            },

            plotOptions: {
              pie: {
                donut: {
                  size: "78%",
                },
                dataLabels: {
                  offset: -40,
                },
              },
            },

            dataLabels: {
              enabled: true,
              formatter: (val) => `${Math.round(val)}%`,
              style: {
                fontSize: "13px",
                fontWeight: 600,
                colors: ["#1F2937"],
              },
              background: {
                enabled: false,
              },
              dropShadow: {
                enabled: false,
              },
            },

            stroke: {
              width: 0,
            },
          }}
        />
      )}
    </div>
  );
};


const DashboardDefault = () => {
  const [phasesData, setPhasesData] = useState([]);
  const [pieData, setPieData] = useState([]);
  const [subjectCount, setSubjectCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const navigate = useNavigate();

  const LoginData = useSelector((state) => state.loginData);
  const role = LoginData.roles;
  const role_id = LoginData.role_id
  const userAuthority = LoginData?.auth || [];
  const hasTrialsAccess = getUserHasAccess(userAuthority, "trials", 9);
  const noPhasesData = !loading && phasesData.length === 0;
  const noStatsData = !loading && pieData.length === 0;

  const getPhaseLisitng = () => {
    if (!hasTrialsAccess) return;

    // Fetch phases list
    DashboardApi.getAllPhaseList(6, 1, role_id, true)
      .then((res) => {
        setSubjectCounts((prev) => ({
          ...prev,
          ...res,
          page_details: res?.page_details
        }));
        if (res?.data) setPhasesData(res.data);
      })
      .catch(console.error);
  };

  // Fetch dashboard data from getDashboardData API
  const fetchDashboardData = () => {
    DashboardApi.getDashboardData()
      .then((res) => {
        setDashboardData(res.data);
        
        // Set total phase and patient counts
        setSubjectCounts((prev) => ({
          ...prev,
          total_phase: res.data?.total_phase,
          total_patient: res.data?.total_patient,
          page_details: { no_of_records: res.data?.total_phase }
        }));
        // Set phase statistics for pie chart
        const phaseStats = res.data?.phase_statistics;
        if (phaseStats) {
          const COLORS = {
            Trial: "#6C5CE7",
            PMS: "#d8a13b",
            Commercial: "#27AE60",
          };

          setPieData(
            Object.keys(phaseStats).map((key) => ({
              name: key,
              percentage: phaseStats[key].percentage,
              count: phaseStats[key].count,
              color: COLORS[key] || "#BDC3C7",
            }))
          );
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!hasTrialsAccess) {
      setLoading(false); 
      return;
    }

    getPhaseLisitng();
  }, [hasTrialsAccess]);
  const handleViewAll = () => {
    if (role_id === 1) {
      navigate(`/products`)
    } else {
      navigate(`/phases`)
    }
  }
  useEffect(()=>{
  fetchDashboardData();
},[])

const phaseChartData = {
  seriesData: pieData.map((item) => item.percentage),
  countData: pieData.map((item) => item.count),
  optionsData: {
    labels: pieData.map((item) => item.name),
    colors: pieData.map((item) => item.color),
    legend: { show: false },
    stroke: { width: 2 },
  },
};



  return (
    <Box sx={{ m: 3 }}>
      {/* USER BAR */}
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "12px",
          padding: "16px 24px",
          boxShadow: "0px 2px 6px rgba(0,0,0,0.04)",
        }}
      >
        <Grid
          container
          alignItems="center"
          wrap="nowrap"
        >
          {/* User name */}
          <Grid item sx={{ flexGrow: 1 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <UserIcon Height="50" Width="50" />
              <Box>
                <Typography fontWeight={600}>
                  {loading ? <Skeleton width={120} /> : role}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Phase counts */}
          {hasTrialsAccess && (
            <Grid item>
              <Box display="flex" alignItems="center" gap={3}>
                <Box textAlign="center">
                  <Typography variant="body2" color="text.secondary">
                    Phases
                  </Typography>
                  <Typography fontWeight={700}>
                    {loading ? (
                      <Skeleton width={40} />
                    ) : (
                      subjectCount?.page_details?.no_of_records
                    )}
                  </Typography>
                </Box>

                <Divider orientation="vertical" flexItem />

                <Box textAlign="center">
                  <Typography variant="body2" color="text.secondary">
                    Total Patients
                  </Typography>
                  <Typography fontWeight={700}>
                    {loading ? (
                      <Skeleton width={40} />
                    ) : (
                      subjectCount?.total_patient
                    )}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>


      {/* PHASE CARDS */}
      {hasTrialsAccess && (
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: "12px",
                  boxShadow: "0px 2px 6px rgba(0,0,0,0.04)",
                  minHeight: "25rem",
                  background: "white",
                  height:"100%",
                }}
              >
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    onClick={handleViewAll}
                  >
                    <Typography variant="h6" fontWeight={600}>
                      Phases
                    </Typography>
                    <Typography
                      sx={{
                        cursor: "pointer",
                        color: "#3571FF",
                        fontWeight: 600,
                      }}
                    >
                      View All
                    </Typography>
                  </Box>

                  <Divider sx={{ mb: 2, mt: 2 }} />

                  <Grid container spacing={2}>
                    {loading
                      ? Array.from({ length: 6 }).map((_, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Skeleton
                            variant="rounded"
                            height={156}
                            sx={{ borderRadius: "10px" }}
                          />
                        </Grid>
                      ))
                      : noPhasesData ? (
                        <div className="col-start-4 col-end-13 flex items-center justify-center">
                          <NoDataCard heading="No Data to Display" />
                        </div>
                      ) : (
                        phasesData.map((item, index) => (
                          <Grid item xs={12} sm={6} md={4} key={index}>
                            <Card
                              elevation={0}
                              onClick={() => {
                                if (role_id === 1) {
                                  navigate(
                                    `/products/product-details/${item.product_details?.id}`
                                  );
                                } else {
                                  navigate(
                                    `/phases/phase-details/${item.id}`
                                  );
                                }
                              }}
                              sx={{
                                position: "relative",
                                overflow: "hidden",
                                cursor: "pointer",
                                height: 156,
                                borderRadius: "10px",
                                background:
                                  "linear-gradient(180deg, #FFFFFF 0%, #F9F9F9 40%)",
                                border: "2px solid rgba(255, 255, 255, 0.7)",
                                boxShadow:
                                  "0px 4px 8px rgba(0,0,0,0.06)",
                                "&::before": {
                                  content: '""',
                                  position: "absolute",
                                  inset: -40,
                                  background: `
                                radial-gradient(120px 120px at 20% 10%, rgba(111, 207, 151, 0.35), transparent 60%),
                                radial-gradient(160px 160px at 55% 0%, rgba(255, 153, 153, 0.45), transparent 65%),
                                radial-gradient(140px 140px at 85% 10%, rgba(120, 145, 255, 0.45), transparent 60%)
                              `,
                                  filter: "blur(60px)",
                                  zIndex: 0,
                                },
                                "& > *": {
                                  position: "relative",
                                  zIndex: 1,
                                },
                              }}
                            >
                              <CardContent
                                sx={{
                                  height: "100%",
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "space-between",
                                }}
                              >
                                {/* TOP CONTENT */}
                                <Box>
                                  <div className={'flex gap-2'}>
                                  <Chip
                                    label={item.phase_status_details?.status}
                                    size="small"
                                    color={
                                      item.phase_status_details?.status ===
                                        "Enrollment Completed"
                                        ? "success"
                                        : "warning"
                                    }
                                  />
                                  <Chip
                                    label={item.phase_type_details?.name}
                                    size="small"
                                    color={
                                      item.phase_status_details?.status ===
                                        "Enrollment Completed"
                                        ? "success"
                                        : "warning"
                                    }
                                  />
                                  </div>
                                  <Typography
                                    color="text.secondary"
                                    fontSize={10}
                                    sx={{
                                      display: "-webkit-box",
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                    }}
                                  >
                                  {item.phase_code}   
                                  </Typography>
                                  <Typography fontWeight={600}>
                                   {item.product_details?.name}: {item.title}
                                  </Typography>
                                </Box>

                                {/* BOTTOM CONTENT */}
                                <Box>
                                  <Divider sx={{ mb: 1 }} />

                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                  >
                                    <Box>
                                      <Typography
                                        fontSize={12}
                                        color="text.secondary"
                                      >
                                        Phase Started
                                      </Typography>
                                      <Typography fontWeight={600}>
                                        {item.phase_start_date
                                          ? moment(
                                            item.phase_start_date
                                          ).format("DD/MM/YYYY")
                                          : "-"}
                                      </Typography>
                                    </Box>

                                    <Box textAlign="right">
                                      <Typography
                                        fontSize={12}
                                        color="text.secondary"
                                      >
                                        Total Patients
                                      </Typography>
                                      <Typography fontWeight={600}>
                                        {item.subjectCount}
                                      </Typography>
                                    </Box>
                                  </Stack>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))
                      )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* PHASE STATISTICS */}
            <Grid item xs={12} md={4}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  borderRadius: "12px",
                  boxShadow: "0px 2px 6px rgba(0,0,0,0.04)",
                }}
              >
                <CardContent
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography variant="h6" fontWeight={600} mb={2}>
                    Phase Statistics
                  </Typography>
                  <Divider />

                  <Box
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {loading ? (
                      <Skeleton variant="circular" width={280} height={280} />
                    ) : noStatsData ? (
                      <NoDataCard heading="No Data to Display" />
                    ) : (
                      <CardChart data={phaseChartData} />
                    )}
                  </Box>

                  <Stack
                    direction="row"
                    justifyContent="center"
                    spacing={3}
                    mt={2}
                  >
                    {loading
                      ? [1, 2, 3].map((i) => (
                        <Skeleton key={i} width={60} />
                      ))
                      : pieData.map((item) => (
                        <Stack
                          key={item.name}
                          direction="row"
                          spacing={1}
                          alignItems="center"
                        >
                          <Box
                            width={10}
                            height={10}
                            borderRadius="50%"
                            bgcolor={item.color}
                          />
                          <Typography fontSize={14}>
                            {item.name}
                          </Typography>
                        </Stack>
                      ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}
      <PatientDetailsStats dashboardData={dashboardData}
        loading={loading} />
      <TabsWithActivity 
        dashboardData={dashboardData}
        loading={loading}
      />
      <ActionButtons />
    </Box>
  );
};

export default DashboardDefault;
